import { api } from "@/lib/api";
import { cn, relativeTime } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Loader2, RefreshCw, Send, User } from "lucide-react";
import { useRef, useState } from "react";

// ============================================================
// Booking AI Tab — Summary + Chat Interface
// ============================================================

interface ChatMessage {
	id: string;
	role: string;
	content: string;
	createdAt: string;
}

interface AnalysisResult {
	summary: string;
	suggestedAction: string;
	confidence: number;
	intent: string | null;
}

export function AITab({
	bookingId,
	booking,
}: {
	bookingId: string;
	booking: Record<string, unknown>;
}) {
	const queryClient = useQueryClient();
	const [message, setMessage] = useState("");
	const chatEndRef = useRef<HTMLDivElement>(null);

	const threadQuery = useQuery({
		queryKey: ["booking", bookingId, "thread"],
		queryFn: () =>
			api.get<{ data: ChatMessage[] }>(`/bookings/${bookingId}/thread`),
	});

	const analyzeMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: AnalysisResult }>(`/bookings/${bookingId}/analyze`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
		},
	});

	const chatMutation = useMutation({
		mutationFn: (msg: string) =>
			api.post<{ data: ChatMessage }>(`/bookings/${bookingId}/chat`, {
				message: msg,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["booking", bookingId, "thread"],
			});
			setMessage("");
			setTimeout(
				() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }),
				100,
			);
		},
	});

	const thread = threadQuery.data?.data ?? [];
	const hasSummary = !!booking.aiSummary;

	const handleSend = () => {
		const msg = message.trim();
		if (!msg || chatMutation.isPending) return;
		chatMutation.mutate(msg);
	};

	return (
		<div className="space-y-4">
			{/* AI Summary Card */}
			<div className="card space-y-3">
				<div className="flex items-center justify-between">
					<h3 className="font-medium text-stone-700 flex items-center gap-2">
						<Bot size={16} className="text-brand-600" />
						AI-Analyse
					</h3>
					<button
						type="button"
						onClick={() => analyzeMutation.mutate()}
						disabled={analyzeMutation.isPending}
						className="btn-secondary !py-1 !px-2 text-xs"
					>
						{analyzeMutation.isPending ? (
							<Loader2 size={12} className="animate-spin" />
						) : (
							<RefreshCw size={12} />
						)}
						{analyzeMutation.isPending
							? "Analysiert..."
							: hasSummary
								? "Neu analysieren"
								: "Jetzt analysieren"}
					</button>
				</div>

				{hasSummary ? (
					<>
						<p className="text-sm text-stone-600 leading-relaxed">
							{String(booking.aiSummary)}
						</p>
						{booking.aiSuggestedAction && (
							<div className="bg-brand-50 border border-brand-100 rounded-md px-3 py-2">
								<p className="text-xs font-medium text-brand-700">
									Empfohlene Aktion
								</p>
								<p className="text-sm text-brand-800 mt-0.5">
									{String(booking.aiSuggestedAction)}
								</p>
							</div>
						)}
						{booking.aiConfidence != null && (
							<div className="flex items-center gap-2">
								<ConfidenceBar value={booking.aiConfidence as number} />
								<span className="text-xs text-stone-400">
									{Math.round((booking.aiConfidence as number) * 100)}%
									Konfidenz
								</span>
							</div>
						)}
					</>
				) : (
					<p className="text-sm text-stone-400">
						Noch keine AI-Analyse vorhanden. Klicke &quot;Jetzt
						analysieren&quot; um eine Einschätzung zu erhalten.
					</p>
				)}
			</div>

			{/* Chat Interface */}
			<div className="card !p-0 overflow-hidden">
				<div className="px-4 py-3 border-b border-stone-100 bg-stone-50/50">
					<h3 className="text-sm font-medium text-stone-700">
						AI-Chat zu dieser Buchung
					</h3>
					<p className="text-xs text-stone-400 mt-0.5">
						Frage den Assistenten zu Details, nächsten Schritten oder
						Empfehlungen.
					</p>
				</div>

				{/* Messages */}
				<div className="max-h-96 overflow-y-auto px-4 py-3 space-y-3">
					{thread.length === 0 && !chatMutation.isPending && (
						<p className="text-xs text-stone-400 text-center py-4">
							Noch keine Nachrichten. Starte eine Konversation.
						</p>
					)}

					{thread.map((msg) => (
						<ChatBubble key={msg.id} message={msg} />
					))}

					{chatMutation.isPending && (
						<div className="flex items-start gap-2">
							<div className="shrink-0 w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center">
								<Bot size={12} className="text-brand-600" />
							</div>
							<div className="bg-stone-50 rounded-lg px-3 py-2">
								<Loader2 size={14} className="animate-spin text-stone-400" />
							</div>
						</div>
					)}

					<div ref={chatEndRef} />
				</div>

				{/* Input */}
				<div className="border-t border-stone-100 px-4 py-3">
					<div className="flex gap-2">
						<input
							type="text"
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									handleSend();
								}
							}}
							placeholder="Frage stellen..."
							className="input flex-1 !py-1.5 text-sm"
							disabled={chatMutation.isPending}
						/>
						<button
							type="button"
							onClick={handleSend}
							disabled={!message.trim() || chatMutation.isPending}
							className="btn-primary !py-1.5 !px-3"
						>
							<Send size={14} />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

// ============================================================
// Chat Bubble
// ============================================================

function ChatBubble({ message }: { message: ChatMessage }) {
	const isUser = message.role === "user";

	return (
		<div className={cn("flex items-start gap-2", isUser && "flex-row-reverse")}>
			<div
				className={cn(
					"shrink-0 w-6 h-6 rounded-full flex items-center justify-center",
					isUser ? "bg-stone-200" : "bg-brand-100",
				)}
			>
				{isUser ? (
					<User size={12} className="text-stone-600" />
				) : (
					<Bot size={12} className="text-brand-600" />
				)}
			</div>
			<div
				className={cn(
					"rounded-lg px-3 py-2 max-w-[80%]",
					isUser ? "bg-stone-100" : "bg-brand-50",
				)}
			>
				<p className="text-sm text-stone-700 whitespace-pre-wrap">
					{message.content}
				</p>
				<p className="text-[10px] text-stone-400 mt-1">
					{relativeTime(message.createdAt)}
				</p>
			</div>
		</div>
	);
}

// ============================================================
// Confidence Bar
// ============================================================

function ConfidenceBar({ value }: { value: number }) {
	const pct = Math.round(value * 100);
	return (
		<div className="flex-1 max-w-32 h-1.5 bg-stone-100 rounded-full overflow-hidden">
			<div
				className={cn(
					"h-full rounded-full transition-all",
					pct >= 80
						? "bg-green-500"
						: pct >= 50
							? "bg-amber-500"
							: "bg-red-400",
				)}
				style={{ width: `${pct}%` }}
			/>
		</div>
	);
}
